import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import { Copy, Eye, EyeOff, Key, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/services/api";
import {
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyListResponse,
} from "@/types";

export const ApiKeysPage = () => {
  const queryClient = useQueryClient();
  const { fetchProfile } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(["read"]);
  const [latestIssuedKey, setLatestIssuedKey] = useState<string | null>(null);
  const [showIssuedKey, setShowIssuedKey] = useState(false);
  const [visibleKeyIds, setVisibleKeyIds] = useState<Record<string, boolean>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get<ApiKeyListResponse>("/auth/api-keys"),
  });

  const createMutation = useMutation({
    mutationFn: (payload: ApiKeyCreateRequest) =>
      api.post<ApiKeyCreateResponse>("/auth/api-keys", payload),
    onSuccess: async (response) => {
      setLatestIssuedKey(response.api_key);
      setShowIssuedKey(true);
      setShowCreateForm(false);
      setNewKeyName("");
      setSelectedPermissions(["read"]);
      await queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      await fetchProfile();
      toast.success("APIキーを発行しました");
    },
    onError: (mutationError) => {
      console.error("Create API key error:", mutationError);
      toast.error("APIキーの発行に失敗しました");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => api.delete(`/auth/api-keys/${keyId}`),
    onSuccess: async (_, keyId) => {
      setVisibleKeyIds((current) => {
        const next = { ...current };
        delete next[keyId];
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      await fetchProfile();
      toast.success("APIキーを削除しました");
    },
    onError: (mutationError) => {
      console.error("Delete API key error:", mutationError);
      toast.error("APIキーの削除に失敗しました");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (keyId: string) => api.put(`/auth/api-keys/${keyId}/toggle`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      await fetchProfile();
      toast.success("APIキーの状態を更新しました");
    },
    onError: (mutationError) => {
      console.error("Toggle API key error:", mutationError);
      toast.error("APIキーの状態変更に失敗しました");
    },
  });

  const apiKeys = data?.api_keys || [];

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("クリップボードにコピーしました");
    } catch (copyError) {
      console.error("Copy error:", copyError);
      toast.error("コピーに失敗しました");
    }
  };

  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();
    if (!newKeyName.trim()) {
      toast.error("APIキー名を入力してください");
      return;
    }
    createMutation.mutate({
      name: newKeyName.trim(),
      permissions: selectedPermissions,
    });
  };

  const togglePermission = (permission: string) => {
    setSelectedPermissions((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission]
    );
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeyIds((current) => ({
      ...current,
      [keyId]: !current[keyId],
    }));
  };

  const relativeTime = (value?: string) => {
    if (!value) return "未使用";
    try {
      return formatDistanceToNow(new Date(value), {
        addSuffix: true,
        locale: ja,
      });
    } catch {
      return "不明";
    }
  };

  const permissionBadge = (permission: string) => {
    const styles: Record<string, string> = {
      read: "bg-emerald-100 text-emerald-700",
      write: "bg-amber-100 text-amber-700",
      admin: "bg-rose-100 text-rose-700",
    };

    return (
      <span
        key={permission}
        className={`rounded-full px-2.5 py-1 text-xs font-medium ${styles[permission] || "bg-slate-100 text-slate-700"}`}
      >
        {permission}
      </span>
    );
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center">
          <Key className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            APIキーの取得に失敗しました
          </h3>
          <p className="text-gray-600">しばらく待ってから再読み込みしてください。</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">APIキー管理</h1>
            <p className="text-gray-600">
              Widget 埋め込みと管理APIで使うキーを発行・停止・削除します。
            </p>
          </div>
          <Button onClick={() => setShowCreateForm((current) => !current)}>
            <Plus className="mr-2 h-4 w-4" />
            新しいAPIキー
          </Button>
        </div>

        {latestIssuedKey ? (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">新しく発行したAPIキー</h3>
            </CardHeader>
            <CardBody className="space-y-3">
              <p className="text-sm text-gray-600">
                この値は再表示できない前提です。必要なら安全な場所へ保存してください。
              </p>
              <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4 md:flex-row md:items-center md:justify-between">
                <code className="break-all text-sm text-gray-800">
                  {showIssuedKey ? latestIssuedKey : "•".repeat(Math.max(latestIssuedKey.length, 20))}
                </code>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowIssuedKey((current) => !current)}>
                    {showIssuedKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => copyText(latestIssuedKey)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {showCreateForm ? (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">APIキーを発行</h3>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleCreate} className="space-y-5">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">キー名</label>
                  <input
                    value={newKeyName}
                    onChange={(event) => setNewKeyName(event.target.value)}
                    placeholder="例: production-widget"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">権限</p>
                  <div className="flex flex-wrap gap-3">
                    {["read", "write", "admin"].map((permission) => (
                      <label
                        key={permission}
                        className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => togglePermission(permission)}
                        />
                        {permission}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" loading={createMutation.isPending}>
                    発行する
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowCreateForm(false)}>
                    キャンセル
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">登録済みAPIキー</h3>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : apiKeys.length === 0 ? (
              <p className="text-sm text-gray-500">
                まだ API キーがありません。Widget 配信や管理APIに必要なキーを発行してください。
              </p>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((apiKey: ApiKey) => (
                  <div
                    key={apiKey.id}
                    className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between"
                  >
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-gray-900">{apiKey.name}</p>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            apiKey.is_active
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {apiKey.is_active ? "有効" : "無効"}
                        </span>
                      </div>
                      <code className="block break-all text-sm text-gray-500">
                        {apiKey.api_key
                          ? visibleKeyIds[apiKey.id]
                            ? apiKey.api_key
                            : "•".repeat(Math.max(apiKey.api_key.length, 20))
                          : `${apiKey.key_prefix}...`}
                      </code>
                      <div className="flex flex-wrap gap-2">{apiKey.permissions.map(permissionBadge)}</div>
                      <p className="text-xs text-gray-500">
                        作成: {relativeTime(apiKey.created_at)} / 最終利用: {relativeTime(apiKey.last_used_at)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {apiKey.api_key ? (
                        <>
                          <Button
                            variant="ghost"
                            onClick={() => toggleKeyVisibility(apiKey.id)}
                          >
                            {visibleKeyIds[apiKey.id] ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => copyText(apiKey.api_key as string)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </>
                      ) : null}
                      <Button
                        variant="ghost"
                        onClick={() => toggleMutation.mutate(apiKey.id)}
                        disabled={toggleMutation.isPending}
                      >
                        <Power className="mr-2 h-4 w-4" />
                        {apiKey.is_active ? "停止" : "有効化"}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => deleteMutation.mutate(apiKey.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </DashboardLayout>
  );
};
